import _ from "lodash";
import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { connect } from "react-redux";
import { compose } from "lib/apolloClient";
import classes from "./TheatreModeV2.scss";
import FullScreenLoader from "UIComponents/FullScreenLoader";
import I18nHOC from "UIComponents/I18nHOC";
import UIModal from "UIComponents/UIModal";
import {
  getAttachmentType,
  GetTheatreModeType,
} from "Utils/GetTheatreModeType";
import {
  downloadFromUrl,
  openLinkInNewTab,
  hideIntercom,
  showIntercom,
} from "modules/Services";
import { getPrintFile } from "modules/PrintThunks";
import ViewerComponent from "./ViewerComponent";
import PropTypes from "prop-types";
import PdfTronView from "./PdfTronView";
import OptionsToolBar from "./OptionsToolBar";
import {
  ChevronLeftOutlined,
  ChevronRightOutlined,
  RightArrowOutlined,
  LeftArrowOutlined,
} from "@toddle-edu/ds-icons";
import {
  setToastMsg,
  enableGlobalHeader,
  disableGlobalHeader,
} from "Login/modules/LoginModule";
import classNames from "classnames";
import { PdfTronViewerV3 } from "AppComponents/PdfTronViewer";
import Workbook from "AppComponents/Workbook";
import { getWorkbookModeForMediaModal, PDFTRON_VIEWER_MODE_TYPES } from "Utils";
import ACLStore from "lib/aclStore";
import { getIsLearningCoursesEnabled } from "LearningCourses/modules/Utils";
import { getSelectedLearningCourseId } from "LearningCourse/modules/Utils";

import { EmptyState, IconButton, Tooltip } from "@toddle-edu/ds-web";

import { EvidenceIllustration } from "@toddle-edu/ds-theme";
import {
  getSelectedCurriculumId,
  getCurrentCurriculumProgramType,
  getSelectedCurriculumProgram,
} from "modules/CommonSelectors";
import WorksheetToolsHeader from "Worksheet/components/WorksheetToolsHeader";
import {
  isPortfolioParityEnabled,
  getShowYtLinkInTheatreMode,
  getIsYoutubeLink,
} from "AppComponents/Post/utils";
import moment from "moment";
import { isMobileOrTablet, isIphone, isIpad } from "Utils/DeviceDetectHelpers";
import {
  getIsNewGradingSetupEnabled,
  getIsEnableLearningGoalsEvaluationInNewGradingSetup,
} from "ClassRoom/modules/utils";
import { downloadToddleDoc } from "AppComponents/ToddleDoc/Modules/ToddleDocModule";

const googleLinksRegex = /https:\/\/docs.google.com\/(document|spreadsheets|presentation)/;
const googleFormsLinkRegex = /https:\/\/docs\.google\.com\/forms\//;
const BLANK_ARRAY = [];
const BLANK_OBJECT = {};
const getToolTip = ({ label = "", keys = [], t }) => {
  return (
    <div className={classes.tooltipContainer}>
      <div className="pr-1">{label}</div>
      {_.map(keys, (key, index) => {
        const renderKey = _.isFunction(key) ? key() : key;
        if (index == 0)
          return (
            <div className={classes.keyWrapper} key={key}>
              {renderKey}
            </div>
          );
        return (
          <div className={classes.keysTooltipContainer} key={key}>
            <div className={classes.divider}></div>
            <div className={classes.keyWrapper}>{renderKey}</div>
          </div>
        );
      })}
    </div>
  );
};

const getDynamicStyles = ({
  mediaAttachments,
  activeIndex,
  totalItems,
  isMobile,
}) => {
  const styleObject = {};
  const attachmentType = getAttachmentType(
    _.get(mediaAttachments, activeIndex, BLANK_OBJECT)
  );

  if (isMobile) {
    if (totalItems === 1) styleObject["width"] = "100%";
    if (totalItems >= 1) styleObject["padding"] = "16px";
    return styleObject;
  }

  if (totalItems === 1) styleObject["width"] = "100%";
  if (_.includes(["DOCUMENT", "PDF"], attachmentType)) {
    styleObject["padding"] = totalItems > 1 ? "0px 16px" : "0px";
  } else if (totalItems > 1) styleObject["padding"] = "16px";

  return styleObject;
};

// --- MAIN COMPONENT ---
const TheatreModeV2 = React.memo(props => {
  const parentRef = useRef(null);
  const imageViewerRef = useRef(null); // to call image viewer related functions
  const videoPlayerRef = useRef(null); // to call video player related functions and get info. of current video
  const audioPlayerRef = useRef(null); // to call audio player related functions and get info. of current video
  const [isMounted, setIsMounted] = useState(false);
  const [shouldShowIntercomOnClose, setShouldShowIntercomOnClose] = useState(
    false
  );
  const theatreModeRef = useRef(null);
  const isRoutineRoute = _.includes(window.location.href, "/routines");

  const {
    hideIntercom,
    showIntercom,
    isIntercomAlreadyHidden,
    t,
    enableGlobalHeader,
    disableGlobalHeader,
  } = props;
  useEffect(() => {
    if (!isIntercomAlreadyHidden) {
      setShouldShowIntercomOnClose(true);
      hideIntercom();
    }
    if (!_.isNil(theatreModeRef?.current)) theatreModeRef?.current?.focus();
  }, [isIntercomAlreadyHidden]);

  const {
    preLoadPdfTron,
    pdfTronInstance,
    showSaveButton,
    assessmentToolsEvaluationDetails,
    isWorksheet,
    activeAttachmentIndex,
    portalType,
    isGlobalHeaderVisible,
    testId,
    isActivityPost,
    curriculumProgramId,
    curriculumProgramType,
    learningCourseId,
    curriculumNodeInterfaceType,
    plannerElementSets,
  } = props;
  const [isPdftronViewMode, setIsPdftronViewMode] = useState(!showSaveButton);
  const [activeIndex, setActiveIndex] = useState(
    _.get(props, "activeAttachmentIndex", 0)
  );
  const isMobile = isMobileOrTablet();

  useEffect(() => {
    setActiveIndex(previousActiveIndex => {
      const updatedActiveIndex = _.get(props, "activeAttachmentIndex", 0);
      return previousActiveIndex !== updatedActiveIndex
        ? updatedActiveIndex
        : previousActiveIndex;
    });
  }, [activeAttachmentIndex]);

  useEffect(() => {
    if (props?.setCurrentAttachmentIndex) {
      props?.setCurrentAttachmentIndex(activeIndex);
    }
    if (props?.updateCurrentActiveIndex) {
      props?.updateCurrentActiveIndex(activeIndex);
    }
  }, [activeIndex]);

  const getSidePaneInitialState = () => {
    const mediaQueryMatch = matchMedia("(max-width: 1024px)");
    const isMobileSizeWindow = mediaQueryMatch.matches;
    if (
      isMobileSizeWindow &&
      isRoutineRoute &&
      !_.size(_.get(props, "mediaAttachments.attachments"))
    ) {
      return true;
    }
    if (isMobileSizeWindow && isActivityPost) {
      return true;
    }
    return isMobileSizeWindow ? false : !_.isNil(props?.sidePaneContent);
  };

  const [showSidePane, setShowSidePane] = useState(getSidePaneInitialState);

  // state having array of objects to maintain timestamp of each video/audio last playback time.
  // format - { id : < ATTACHMENT_ID >, timestamp: < last_playback_time > }
  const [videosPlaybackDetails, setVideoPlaybackDetails] = useState([]);
  const [audiosPlaybackDetails, setAudiosPlaybackDetails] = useState([]);
  const [isDownloading, setIsDownloading] = useState(false);
  const [showAttachmentEditor, setShowAttachmentEditor] = useState(false); // used to switch workbook & pdftron in edit mode
  const [rateOnLearningGoalsDetails, setRateOnLearningGoalsDetails] = useState(
    _.get(props, "postRatedLearningGoalsDetails", BLANK_OBJECT)
  );

  const mediaAttachments = _.get(props, "mediaAttachments.attachments");

  // Functions useful for header buttons
  const onExpandViewClick = useCallback(() => {
    if (!_.isNil(props?.sidePaneContent)) setShowSidePane(!showSidePane);
    if (isMounted == false) setIsMounted(true);
  }, [showSidePane]);

  const onRotateImageClick = useCallback(() => {
    if (!_.isNil(imageViewerRef) && !_.isNil(imageViewerRef?.current)) {
      imageViewerRef?.current?.rotate();
    }
  }, [imageViewerRef]);
  const onImageZoomInClick = useCallback(() => {
    if (!_.isNil(imageViewerRef) && !_.isNil(imageViewerRef?.current)) {
      imageViewerRef?.current?.zoomIn(0.1);
    }
  }, [imageViewerRef]);
  const onImageZoomOutClick = useCallback(() => {
    if (!_.isNil(imageViewerRef) && !_.isNil(imageViewerRef?.current)) {
      imageViewerRef?.current?.zoomOut(0.1);
    }
  }, [imageViewerRef]);
  const onDownloadClick = useCallback(attachment => {
    if (
      _.get(attachment, "__typename") == "AssessmentTool" ||
      _.get(attachment, "assessmentToolType", "") == "LEARNING_GOALS"
    ) {
      (() => {
        const id = _.get(
            props,
            "assessmentToolsEvaluationDetails.parentId",
            null
          ),
          type = "studentassignmentv2";
        if (!_.isNil(id)) {
          setIsDownloading(true);
          props
            .getPrintFile({
              id,
              type,
              shouldOpenInWindow: false,
              metadata: {
                isNewGradingSetupEnabled: getIsNewGradingSetupEnabled(),
                isLearningCoursesEnabled: getIsLearningCoursesEnabled(),
                isEnableLearningGoalsEvaluationInNewGradingSetup: getIsEnableLearningGoalsEvaluationInNewGradingSetup(),
                learningCourseId,
                curriculumProgramId,
                curriculumProgramType,
                curriculumNodeInterfaceType,
                plannerElementSets,
              },
            })
            .then(fileUrl => {
              if (!_.isNil(fileUrl) && !_.isEmpty(fileUrl)) {
                setIsDownloading(false);
                props?.downloadFromUrl({
                  attachment: {
                    name: props.t("assessmentEvaluation:person_evaluation", {
                      name: _.get(
                        props,
                        "assessmentToolsEvaluationDetails.studentDetails.firstName",
                        "student"
                      ),
                    }),
                    url: fileUrl,
                    mimeType: "application/pdf",
                  },
                  shouldAskForSave: true,
                  parentType: "studentassignmentv2",
                  parentId: _.get(
                    props,
                    "assessmentToolsEvaluationDetails.parentId",
                    null
                  ),
                });
              }
            });
        }
      })();
    } else if (
      !_.isEmpty(attachment) &&
      _.isEqual(attachment?.type, "TODDLE_DOC")
    ) {
      props?.downloadToddleDoc({
        attachment,
        shouldAskForSave: true,
      });
    } else if (
      (isIphone() || isIpad()) &&
      _.get(attachment, "mimeType") === "application/pdf" &&
      !_.isNil(attachment?.url)
    ) {
      props?.openLinkInNewTab?.({ url: attachment.url });
    } else {
      if (!_.isNil(props?.downloadFromUrl) && !_.isNil(attachment?.url))
        props?.downloadFromUrl({
          attachment: attachment,
          shouldAskForSave: true,
          ...(props?.downloadAudioWithName &&
          _.isEqual(_.get(attachment, "type"), "AUDIO")
            ? {
                name: attachment?.name
                  ? attachment?.name
                  : "toddle_audio_" + moment().format("DD-MM-YY_h_mm"),
              }
            : {}),
        });
    }
  }, []);

  const onEditButtonClick = activeAttachment => {
    if (
      _.get(props, "mode") == "edit" ||
      _.get(props, "showEditResponseButtonOverride")
    ) {
      props?.onEditAttachmentClick?.(activeAttachment);
      //setShowAttachmentEditor(true);
    }
  };

  // specifically for google docs/slides/sheets links
  const onOpenLinkInNewTabClick = activeAttachment => {
    const isMicrosoftTemplate =
      _.get(activeAttachment, "metadata.serviceType", "") == "MICROSOFT" &&
      _.has(activeAttachment, "metadata.permission");

    const attachmentUrl = isMicrosoftTemplate
      ? !_.isNil(_.get(activeAttachment, "metadata.viewUrl"))
        ? _.get(activeAttachment, "metadata.viewUrl")
        : _.get(props, "userInfo.user_type", "") === "staff"
        ? _.get(activeAttachment, "url")
        : undefined
      : _.get(activeAttachment, "url");

    const sourceUrl = _.get(activeAttachment, "url");
    if (
      googleLinksRegex.test(sourceUrl) ||
      googleFormsLinkRegex.test(sourceUrl)
    ) {
      props?.openLinkInNewTab?.({ url: attachmentUrl });
    }
  };
  // return configurations object, which is used to render media-specific options

  const currentActiveAttachment = useMemo(() => {
    return activeIndex < _.size(mediaAttachments, 0)
      ? mediaAttachments[activeIndex]
      : _.get(
          props,
          [
            "assessmentTools",
            activeIndex -
              _.size(_.get(props, "mediaAttachments.attachments"), 0),
          ],
          BLANK_OBJECT
        );
  }, [activeIndex, mediaAttachments, _.get(props, "assessmentTools")]);

  const isToddleDocDownloadWithCommentsEnabled = ACLStore.can(
    "FeatureFlag:EnableDownloadToddleDocWithComments"
  );

  const onToddleDocDownloadClick = useCallback(
    ({ attachment, includeComments }) => {
      props?.downloadToddleDoc({
        attachment,
        shouldAskForSave: true,
        includeComments,
      });
    },
    [props?.downloadToddleDoc]
  );

  const configurations = useMemo(() => {
    const config = {
      attachment: currentActiveAttachment,
      isExpandedView: !showSidePane,
      showExpandOption: !_.isNil(props?.sidePaneContent),
      onExpandViewClick,
      onRotateImageClick,
      onImageZoomInClick,
      onImageZoomOutClick,
      onDownloadClick,
      onEditButtonClick,
      onOpenLinkInNewTabClick,
    };
    if (isToddleDocDownloadWithCommentsEnabled) {
      config.onToddleDocDownloadClick = onToddleDocDownloadClick;
    }
    return config;
  }, [
    showSidePane,
    currentActiveAttachment,
    onToddleDocDownloadClick,
    isToddleDocDownloadWithCommentsEnabled,
  ]);

  const isRateOnLearningGoalsPresent =
    _.size(
      _.get(
        props,
        "postRatedLearningGoalsDetails.propsIbPypElementRatings",
        BLANK_ARRAY
      )
    ) > 0;

  const totalItems =
    _.size(mediaAttachments) +
    _.size(_.get(props, "assessmentTools", BLANK_ARRAY)) +
    (isRateOnLearningGoalsPresent ? 1 : 0);

  // Function to update the state of video/audio playback time array.
  const getPlaybackTimeForState = ({ attachment, currentState }) => {
    const type = _.get(attachment, "type");
    const isYoutubeLink =
      getShowYtLinkInTheatreMode() &&
      _.isEqual(type, "LINK") &&
      getIsYoutubeLink(_.get(attachment, "url"));

    if (_.includes(["VIDEO", "AUDIO"], type) || isYoutubeLink) {
      const copiedState = _.cloneDeep(currentState);
      const mediaRef =
        type == "VIDEO" || isYoutubeLink ? videoPlayerRef : audioPlayerRef;
      const existingMediaStateIndex = _.findIndex(
        copiedState,
        media => media?.id == _.get(attachment, "id")
      );

      if (existingMediaStateIndex >= 0) {
        if (!_.isNil(mediaRef?.current)) {
          copiedState[
            existingMediaStateIndex
          ].timestamp = mediaRef?.current?.getCurrentTime();
        }
      } else {
        if (!_.isNil(mediaRef?.current)) {
          const newMediaState = {
            id: _.get(attachment, "id"),
            timestamp: mediaRef?.current?.getCurrentTime() ?? 0,
          };
          copiedState.push(newMediaState);
        }
      }
      return copiedState;
    }
    return currentState;
  };
  const updateVideoPlaybackState = activeAttachment => {
    setVideoPlaybackDetails(currentState => {
      return getPlaybackTimeForState({
        attachment: activeAttachment,
        currentState,
      });
    });
  };
  const updateAudioPlaybackState = activeAttachment => {
    setAudiosPlaybackDetails(currentState => {
      return getPlaybackTimeForState({
        attachment: activeAttachment,
        currentState,
      });
    });
  };
  const onNavigateLeft = useCallback(
    e => {
      e?.stopPropagation?.();
      const activeAttachment = _.get(
        mediaAttachments,
        [activeIndex],
        BLANK_OBJECT
      );
      if (activeIndex - 1 < 0) return;
      // save playback time if current attachment is video/audio
      if (_.get(activeAttachment, "type", "") == "VIDEO") {
        updateVideoPlaybackState(activeAttachment);
      }
      if (_.get(activeAttachment, "type", "") == "AUDIO") {
        updateAudioPlaybackState(activeAttachment);
      }
      if (isRateOnLearningGoalsPresent && activeIndex === totalItems - 1) {
        setActiveIndex(
          activeIndex -
            (_.size(_.get(props, "assessmentTools", BLANK_ARRAY)) || 1)
        );
      } else if (activeIndex >= _.size(mediaAttachments)) {
        setActiveIndex(_.size(mediaAttachments, 1) - 1);
      } else {
        setActiveIndex(activeIndex - 1);
      }
    },
    [activeIndex, setActiveIndex]
  );
  const onNavigateRight = useCallback(
    e => {
      e?.stopPropagation?.();
      const activeAttachment = _.get(
        mediaAttachments,
        [activeIndex],
        BLANK_OBJECT
      );
      if (activeIndex + 1 >= totalItems) return;
      // save playback time if current attachment is video/audio
      if (_.get(activeAttachment, "type", "") == "VIDEO") {
        updateVideoPlaybackState(activeAttachment);
      }
      if (_.get(activeAttachment, "type", "") == "AUDIO") {
        updateAudioPlaybackState(activeAttachment);
      }
      if (
        activeIndex >= _.size(mediaAttachments) &&
        isRateOnLearningGoalsPresent
      ) {
        setActiveIndex(totalItems - 1);
      } else {
        setActiveIndex(activeIndex + 1);
      }
    },
    [activeIndex, totalItems, setActiveIndex]
  );

  // Function can be used by sidePaneContent component to set specific index of any attachment directly
  const setTheatreAttachmentIndex = useCallback(
    index => {
      const activeAttachment = _.get(
        mediaAttachments,
        [activeIndex],
        BLANK_OBJECT
      );
      if (_.get(activeAttachment, "type", "") == "VIDEO") {
        updateVideoPlaybackState(activeAttachment);
      }
      if (_.get(activeAttachment, "type", "") == "AUDIO") {
        updateAudioPlaybackState(activeAttachment);
      }
      if (index < totalItems) setActiveIndex(index);
    },
    [mediaAttachments]
  );

  const handleKeyPress = useCallback(
    event => {
      const keyPressed = event?.key;
      const targetElement = event?.target?.tagName;
      if (!isPdftronViewMode) return;
      if (_.includes(["TEXTAREA", "INPUT"], targetElement)) return; // prevent navigation if user is typing in some input field.
      switch (keyPressed) {
        case "a":
        case "ArrowLeft":
          onNavigateLeft();
          break;
        case "d":
        case "ArrowRight":
          onNavigateRight();
          break;
      }
    },
    [onNavigateLeft, onNavigateRight, isPdftronViewMode]
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyPress);

    return () => {
      document.removeEventListener("keydown", handleKeyPress);
    };
  }, [handleKeyPress]);

  const handleMessageFromIframe = useCallback(
    event => {
      const message = event.data;

      switch (message) {
        case "ANNOTATE_FILE": {
          setIsPdftronViewMode(false);
          break;
        }
        case "CANCEL_ANNOTATE":
        case "SAVE_FILE": {
          setIsPdftronViewMode(true);
          break;
        }
        case "CAN_NAVIGATE_PREVIOUS": {
          if (isPdftronViewMode) onNavigateLeft();
          break;
        }
        case "CAN_NAVIGATE_NEXT": {
          if (isPdftronViewMode) onNavigateRight();
          break;
        }
      }
    },
    [onNavigateLeft, onNavigateRight, isPdftronViewMode]
  );

  useEffect(() => {
    window.addEventListener("message", handleMessageFromIframe);
    return () => {
      window.removeEventListener("message", handleMessageFromIframe);
    };
  }, [handleMessageFromIframe]);

  const closeTheatreModePdftron = async () => {
    toggleTheatreModeLocal(false);

    if (pdfTronInstance) {
      const { annotationManager } = pdfTronInstance.Core;
      const annots = annotationManager?.getAnnotationsList();
      /** Export only the updated annotations instead of the initial ones */
      const xfdfString = await annotationManager.exportAnnotations({
        annotList: annots,
      });

      toggleShowTheatreModeV2({
        xfdfString,
        isViewMode: annotationManager.isReadOnlyModeEnabled(),
      });
    }
  };

  const theatreActiveAttachmentId =
    activeIndex < _.size(_.get(props, "mediaAttachments.attachments"), 0)
      ? _.get(props, ["mediaAttachments", "attachments", activeIndex, "id"])
      : _.get(props, [
          "assessmentTools",
          activeIndex - _.size(_.get(props, "mediaAttachments.attachments"), 0),
          "assessmentToolItem",
          "id",
        ]);

  // Function can be used by sidePaneContent component to set specific attachment by id rather than index
  const setTheatreAttachmentById = useCallback(
    ({
      id,
      type,
      customCompareFn,
      elementPYPOfRatings,
      propsIbPypElementRatings,
    }) => {
      if (type == "ATTACHMENT") {
        const targetIndex = _.isFunction(customCompareFn)
          ? _.findIndex(mediaAttachments, item => customCompareFn({ id, item }))
          : _.findIndex(mediaAttachments, attachment => attachment?.id == id);
        if (targetIndex >= 0) setActiveIndex(targetIndex);
      } else if (type == "ASSESSMENT_TOOL") {
        const assessmentTools = _.get(props, "assessmentTools", BLANK_ARRAY);
        const targetIndex = _.isFunction(customCompareFn)
          ? _.findIndex(assessmentTools, item => customCompareFn({ id, item }))
          : _.findIndex(assessmentTools, item => item?.id == id);
        if (
          targetIndex >= 0 &&
          _.size(mediaAttachments) + targetIndex < totalItems
        )
          setActiveIndex(_.size(mediaAttachments) + targetIndex);
      } else if (type === "POST_RATED_LEARNING_GOALS") {
        setRateOnLearningGoalsDetails({
          ...rateOnLearningGoalsDetails,
          elementPYP: elementPYPOfRatings,
          propsIbPypElementRatings: propsIbPypElementRatings,
        });
        setActiveIndex(totalItems - 1);
      }
    },
    [mediaAttachments]
  );

  const showWorksheetToolHeader = !_.isEmpty(
    _.get(
      assessmentToolsEvaluationDetails,
      "worksheetHeaderDetails",
      BLANK_OBJECT
    )
  );

  const sidePaneContainerClasses = classNames({
    [classes.sidePaneScrollContainer]: true,
    [classes.visibleSidePane]: true,
    [classes.visibleSidePaneV2]: isRoutineRoute,
    [classes.visibleSidePaneV2FullWidth]:
      isRoutineRoute && !_.size(mediaAttachments),
    [classes.mobileSidePane]: isMobile,
    [classes.mobileRoutinesSidePane]: isMobile && isRoutineRoute,
    [classes.mobileRoutinesSidePaneFullWidth]:
      isMobile && isRoutineRoute && !_.size(mediaAttachments),
  });

  const activeAttachment = _.get(mediaAttachments, [activeIndex]);
  const activeAttachmentType = _.get(activeAttachment, "type");

  const containerWorksheetViewClassses = classNames({
    [classes.viewerContainer]: true,
    [classes.containerWorksheetView]: showWorksheetToolHeader,
    [classes.positionRelative]: activeAttachmentType === "WORKBOOK",
  });

  const {
    showWorkBook = false,
    fullScreenButtonStatus,
    toggleShowTheatreModeV2,
    xfdfString,
    setPdftronInstance,
    theatreModeV2OverlayContainer,
    allowedPermissions,
    assessmentId,
    assignmentId,
    showEditResponseButtonOverride,
  } = props;

  const isLeftButtonDisabled = () => {
    return activeIndex == 0;
  };

  const isRightButtonDisabled = () => {
    if (isRateOnLearningGoalsPresent) {
      return activeIndex === totalItems - 1;
    }
    return (
      activeIndex == totalItems - 1 ||
      activeIndex >= _.size(_.get(props, "mediaAttachments.attachments"))
    );
  };

  const toggleTheatreModeLocal = stateValue => {
    props?.toggleTheatreMode?.(stateValue);
    if (shouldShowIntercomOnClose) showIntercom();
  };

  const updateRateOnLearningGoalsElements = useCallback(
    ({ elementPYP, propsIbPypElementRatings }) => {
      setRateOnLearningGoalsDetails({
        ...rateOnLearningGoalsDetails,
        elementPYP,
        propsIbPypElementRatings,
      });
    },
    []
  );
  const modalOverlayContainer = classNames({
    [classes.modalOverlayContainer]: true,
    [theatreModeV2OverlayContainer]: !!theatreModeV2OverlayContainer,
  });

  // Note:This will be applicable for all attachments views.
  const navIconBackgroundClass = classNames({
    [classes.navIconBackground]: !isPortfolioParityEnabled(),
    [classes.navIconBackgroundV2]: isPortfolioParityEnabled(),
    ["dark"]: true,
  });

  const onTemplateCreated = () => {
    const { onDiscardDirectly } = props;
    onDiscardDirectly?.();
  };

  const enableSaveWorkbookTemplate = ACLStore.can(
    "FeatureFlag:SaveWorkbookTemplateFromSubmissionAttachment"
  );
  const showEditFrameOption = props.showEditFrameOption || false;
  const templateProps = enableSaveWorkbookTemplate
    ? {
        onTemplateCreated,
        disableArr: ["SAVE_BTN"],
        enableArr: ["SAVE_DROPDOWN"],
        templateMetadata: { curriculumProgramId },
        showEditFrameOption,
      }
    : {
        showEditFrameOption,
      };

  const postRateOnLearningGoalsData = {
    ...rateOnLearningGoalsDetails,
    ...(_.get(props, "learningCourseIdFromProps")
      ? { learningCourseIdFromProps: _.get(props, "learningCourseIdFromProps") }
      : {}),
  };

  return showWorkBook &&
    (activeAttachmentType === "IMAGE" ||
      activeAttachmentType === "WORKBOOK") ? (
    <Workbook
      attachment={activeAttachment}
      workbookMode={getWorkbookModeForMediaModal({
        attachmentType: activeAttachmentType,
        mode: _.get(props, "mode"),
        workbookMode: _.get(props, "workbookMode"),
      })}
      onUpdateSuccessfully={_.get(props, "onWorkbookUpdateSuccessfully")}
      onDiscardDirectly={_.get(props, "onDiscardDirectly")}
      onAfterDiscardDirectly={_.get(props, "onAfterDiscardDirectly")}
      updateAttachments={_.get(props, "updateAttachments")}
      portalType={portalType}
      assessmentId={assessmentId}
      assignmentId={assignmentId}
      shouldShowViewInstructions={true}
      {...templateProps}
    />
  ) : activeAttachmentType == "FILE" &&
    _.isEqual(
      GetTheatreModeType({ media: activeAttachment }),
      "PDFTRONVIEWER"
    ) &&
    _.get(props, "pdftronViewerMode", PDFTRON_VIEWER_MODE_TYPES["VIEWER"]) ==
      PDFTRON_VIEWER_MODE_TYPES["STUDENT_EDITOR"] ? (
    <UIModal isOpen={true} isFullScreen={true} showFromTopOfTheViewPort={true}>
      <PdfTronViewerV3
        hidePdfTronContainer={false}
        file={activeAttachment}
        shouldShowGiveFeedbackButton={
          showAttachmentEditor || _.get(props, "showPdfTronfeedback", false)
        }
        onPdfTronSwitchToEditMode={_.get(props, "onPdfTronSwitchToEditMode")}
        pdftronViewerMode={
          showAttachmentEditor
            ? PDFTRON_VIEWER_MODE_TYPES["STUDENT_EDITOR"]
            : _.get(
                props,
                "pdftronViewerMode",
                PDFTRON_VIEWER_MODE_TYPES["VIEWER"]
              )
        }
        annotationUserInfo={_.get(props, "userInfo")}
        onUpdateSuccessfully={_.get(props, "onPdftronUpdateSuccessfullly")}
        showHeaderOptionsForViewMode={false}
        closeViewer={() => toggleTheatreModeLocal(false)}
        fullScreenButtonStatus={fullScreenButtonStatus}
        toggleShowTheatreModeV2={toggleShowTheatreModeV2}
        showSaveButton={showSaveButton}
        setPdftronInstance={setPdftronInstance}
        xfdfString={xfdfString}
      />
    </UIModal>
  ) : (
    <UIModal
      isOpen={true}
      isFullScreen={true}
      onRequestClose={() => toggleTheatreModeLocal(false)}
      overlayContainer={modalOverlayContainer}
      showFromTopOfTheViewPort={true}
    >
      <div
        className={classes.container}
        ref={theatreModeRef}
        tabIndex={0}
        onClick={e => {
          e.stopPropagation();
        }}
      >
        <div className={classes.headerWithContentContainer}>
          {/* HEADER WITH MEDIA-SPECIFIC INFO. AND OPTIONS */}
          <OptionsToolBar
            {...props}
            activeIndex={activeIndex}
            attachments={mediaAttachments}
            configurations={configurations}
            getToolTip={getToolTip}
            showEditButton={
              (_.get(props, "mode") == "edit" &&
                _.get(props, "showEditResponseButton", false)) ||
              showEditResponseButtonOverride
            }
            toggleTheatreMode={
              pdfTronInstance ? closeTheatreModePdftron : toggleTheatreModeLocal
            }
            userType={_.get(props, "userInfo.user_type", "")}
            isRateOnLearningGoalsPresent={isRateOnLearningGoalsPresent}
            totalItems={totalItems}
            postRatedLearningGoalsDetails={rateOnLearningGoalsDetails}
            setActiveIndex={setActiveIndex}
            allowedPermissions={allowedPermissions}
          />
          <div className={classes.innerContainer}>
            <div
              className={classes.mediaContainer}
              style={getDynamicStyles({
                mediaAttachments,
                activeIndex,
                totalItems,
                isMobile,
              })}
            >
              {totalItems > 0 ? (
                <>
                  {totalItems > 1 &&
                  ((_.size(mediaAttachments) > 0 && isPdftronViewMode) ||
                    isRateOnLearningGoalsPresent) ? (
                    <div className={classes.navigationLeftIcon}>
                      <Tooltip
                        tooltip={getToolTip({
                          label: t("common:previous"),
                          keys: [
                            <LeftArrowOutlined
                              key={"theatre_navigation_previous_icon"}
                              variant="on"
                              size="xxxx-small"
                            />,
                            "A",
                          ],
                          t,
                        })}
                        placement={"topLeft"}
                        theme={"light"}
                      >
                        <IconButton
                          testId={`${testId}-theatremode-navigateLeft`}
                          icon={<ChevronLeftOutlined variant="default" />}
                          onClick={onNavigateLeft}
                          className={navIconBackgroundClass}
                          disabled={isLeftButtonDisabled()}
                          _legacy_theme={"dark"}
                          _legacy_variant={"plain-default"}
                          variant="neutral"
                          type="outlined"
                        />
                      </Tooltip>
                    </div>
                  ) : null}
                  <div
                    ref={parentRef}
                    className={containerWorksheetViewClassses}
                  >
                    {showWorksheetToolHeader ? (
                      <WorksheetToolsHeader
                        {..._.get(
                          assessmentToolsEvaluationDetails,
                          "worksheetHeaderDetails",
                          BLANK_OBJECT
                        )}
                      />
                    ) : null}
                    {preLoadPdfTron ? (
                      <>
                        {isDownloading ? <FullScreenLoader /> : null}
                        <ViewerComponent
                          {...props}
                          activeIndex={activeIndex}
                          theatreModeRef={theatreModeRef}
                          parentRef={parentRef}
                          imageViewerRef={imageViewerRef}
                          videoPlayerRef={videoPlayerRef}
                          audioPlayerRef={audioPlayerRef}
                          videosPlaybackDetails={videosPlaybackDetails}
                          audiosPlaybackDetails={audiosPlaybackDetails}
                          showSidePane={showSidePane}
                          setShowAttachmentEditor={setShowAttachmentEditor}
                          onNavigateRight={onNavigateRight}
                          onNavigateLeft={onNavigateLeft}
                          postRatedLearningGoalsDetails={
                            postRateOnLearningGoalsData
                          }
                          isWorksheet={isWorksheet}
                        />
                        <PdfTronView
                          {...props}
                          activeIndex={activeIndex}
                          theatreModeRef={theatreModeRef}
                        />
                      </>
                    ) : (
                      <ViewerComponent
                        {...props}
                        studentSubmissionId={props?.studentSubmissionId}
                        activeIndex={activeIndex}
                        theatreModeRef={theatreModeRef}
                        parentRef={parentRef}
                        imageViewerRef={imageViewerRef}
                        postRatedLearningGoalsDetails={
                          rateOnLearningGoalsDetails
                        }
                        isWorksheet={isWorksheet}
                      />
                    )}
                  </div>
                  {totalItems > 1 &&
                  ((_.size(mediaAttachments) > 0 && isPdftronViewMode) ||
                    isRateOnLearningGoalsPresent) ? (
                    <div className={classes.navigationRightIcon}>
                      <Tooltip
                        tooltip={getToolTip({
                          label: t("common:next"),
                          keys: [
                            <RightArrowOutlined
                              key={"theatre_navigation_next_icon"}
                              variant="on"
                              size="xxxx-small"
                            />,
                            "D",
                          ],
                          t,
                        })}
                        placement={"topRight"}
                        theme={"light"}
                      >
                        <IconButton
                          testId={`${testId}-theatremode-navigateRight`}
                          icon={<ChevronRightOutlined variant="default" />}
                          onClick={onNavigateRight}
                          className={navIconBackgroundClass}
                          disabled={isRightButtonDisabled()}
                          _legacy_theme={"dark"}
                          _legacy_variant={"plain-default"}
                          variant="neutral"
                          type="outlined"
                        />
                      </Tooltip>
                    </div>
                  ) : null}
                </>
              ) : (
                <div className={classes.noPreviewContainer}>
                  <EmptyState
                    title={props?.t("common:no_media_attached_to_preview")}
                    // Note: removing this for DQC comment
                    // subtitle={props?.t(
                    //   "common:no_media_has_been_attached_to_preview"
                    // )}
                    illustration={EvidenceIllustration}
                    size={"small"}
                  />
                </div>
              )}
            </div>
            {!_.isNil(props?.sidePaneContent) && showSidePane ? (
              <div className={sidePaneContainerClasses}>
                {_.isFunction(props?.sidePaneContent)
                  ? props?.sidePaneContent({
                      setTheatreAttachmentIndex: setTheatreAttachmentIndex,
                      theatreActiveAttachmentIndex: activeIndex,
                      setTheatreAttachmentById: setTheatreAttachmentById,
                      theatreActiveAttachmentId: theatreActiveAttachmentId,
                      updateRateOnLearningGoalsElements: updateRateOnLearningGoalsElements,
                    })
                  : React.cloneElement(props?.sidePaneContent, {
                      setTheatreAttachmentIndex: setTheatreAttachmentIndex,
                      theatreActiveAttachmentIndex: activeIndex,
                      setTheatreAttachmentById: setTheatreAttachmentById,
                      theatreActiveAttachmentId: theatreActiveAttachmentId,
                      updateRateOnLearningGoalsElements: updateRateOnLearningGoalsElements,
                    })}
              </div>
            ) : null}
          </div>

          {/* Note:keeping this for future reference */}
          {/* {totalItems > 1 ? (
            <MediaNavigation
              {...props}
              activeIndex={activeIndex}
              attachments={_.get(props, "mediaAttachments.attachments")}
              onNavigateLeft={onNavigateLeft}
              onNavigateRight={onNavigateRight}
              showNavigation={showSidePane == false}
              getToolTip={getToolTip}
            />
          ) : null} */}
        </div>
      </div>
    </UIModal>
  );
});

TheatreModeV2.displayName = "TheatreModeV2";

const mapStateToProps = (state, ownProps) => {
  const userInfo = _.get(state, "login.userInfo", BLANK_OBJECT);
  const curriculumProgramType =
    getCurrentCurriculumProgramType(state) ||
    _.get(ownProps, "curriculumProgramType");

  const userType = state.login.userInfo.user_type;
  const childId =
    userType == "parent"
      ? _.get(state, "login.userInfo.childID", undefined)
      : undefined;

  const isIntercomAlreadyHidden = !_.get(
    state,
    "app_services.showIntercom",
    true
  );

  const isGlobalHeaderVisible = _.get(
    state,
    "login.globalHeader.isVisible",
    false
  );

  const learningCourseId = getSelectedLearningCourseId(state);

  const selectedCurriculumProgram = getSelectedCurriculumProgram(state);
  const curriculumNodeInterfaceType = _.get(
    selectedCurriculumProgram,
    "nodeInterfaceType",
    ""
  );

  const plannerElementSets = _.get(
    state,
    "platform.currentPlannerElementSets",
    {}
  );

  return {
    userInfo,
    curriculumProgramType,
    childId,
    curriculumProgramId: getSelectedCurriculumId(state),
    isIntercomAlreadyHidden,
    isGlobalHeaderVisible,
    learningCourseId,
    curriculumNodeInterfaceType,
    plannerElementSets,
  };
};

TheatreModeV2.propTypes = {
  mediaAttachments: PropTypes.shape({
    // array of MEDIA attachments - format { attachments: arrayOfattachments }
    attachments: PropTypes.array,
  }),
  testId: PropTypes.string,
  assessmentTools: PropTypes.array, // array of assessment tools (extract tool object if it is under the "node" property in your use-case)
  assessmentToolsEvaluationDetails: PropTypes.object, // Object of props required for various assessment tool
  preLoadPdfTron: PropTypes.bool,
  activeAttachmentIndex: PropTypes.number, // index of attachment/assessment tool to be dispayed initially
  toggleTheatreMode: PropTypes.func, // function for changing your local state to close theatreMode
  iframeIdSelector: PropTypes.string, // Specificly for WORKBOOK, use UNIQUE id which is used by workbook to render iframe node in DOM
  fullScreenButtonStatus: PropTypes.string, // For PDFTron, sends the status of full screen button
  toggleShowTheatreModeV2: PropTypes.func, // For PDFTron, toggles theatreModeV2 in ClassRoomMedia
  showSaveButton: PropTypes.bool, // For PDFTron, shows save button in ClassRoomMedia
  xfdfString: PropTypes.string, // xfdf string to load in PDFTron
  setPdftronInstance: PropTypes.func, // function to set pdftron instance in ClassRoomMedia
  pdfTronInstance: PropTypes.object, // pdftron instance, to handle postMessages to pdftron iframe
};
TheatreModeV2.defaultProps = {
  testId: "media",
  mediaAttachments: { attachments: [] },
  assessmentTools: [],
  assessmentToolsEvaluationDetails: {},
  preLoadPdfTron: true,
  activeAttachmentIndex: 0,
};

const mapActionCreators = {
  downloadFromUrl,
  downloadToddleDoc,
  setToastMsg,
  openLinkInNewTab,
  getPrintFile,
  hideIntercom,
  showIntercom,
  enableGlobalHeader,
  disableGlobalHeader,
};

export default compose(
  I18nHOC({ resource: ["common", "journal"] }),
  connect(mapStateToProps, mapActionCreators)
)(TheatreModeV2);
